import * as React from "react";
import { useLayoutEffect, useState } from "react";
import apiClient from "@/api-client";
// The agent's service form, used unchanged. A persona is configured through exactly the
// same plugin picker and the same plugin-declared settings fields, against the same
// providers — so anything set up once is available to both.
import { Service } from "@/new-pages/agents/create/service";
import { Alert, Box, Button, Card, Divider, Link, Stack, Tab, Tabs } from "@mui/material";
import Typography from "@mui/material/Typography";
import { ArrowLeftIcon } from "@phosphor-icons/react/dist/ssr/ArrowLeft";
import { useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { logger } from "@/lib/default-logger";
import { personasKeys } from "@/hooks/api/use-personas";
import { Breadcrumbs } from "@/components/breadcrumbs/breadcrumbs";
import { ComponentLoader } from "@/components/componentLoader/component-loader";
import { RouterLink } from "@/components/core/link";
import { toast } from "@/components/core/toaster";
import { CustomSkeleton } from "@/components/customSkeleton/custom-skeleton";

import { BasicInformation } from "./basic-information";

const BASIC = "basic-information";

const SERVICE_LABELS: Record<string, string> = {
	stt: "Speech to text",
	llm: "Model",
	tts: "Voice",
};

const SERVICE_HINTS: Record<string, string> = {
	stt: "How this caller hears the agent. Unused in a text-mode run.",
	llm: "This caller's brain. Worth choosing a different family from the agent under test — a caller sharing the agent's model shares its blind spots.",
	tts: "This caller's voice. Give it a different voice from the agent, or recordings become very hard to follow.",
};

export function CreatePersona(): React.JSX.Element {
	const { personaId } = useParams();
	const isEdit = Boolean(personaId);

	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [isLoadingPersona, setIsLoadingPersona] = useState(isEdit);
	const [isLoadingPlugins, setIsLoadingPlugins] = useState(true);
	const [personaName, setPersonaName] = useState<string>("");
	const [languages, setLanguages] = useState<string[]>([]);
	const [codeSwitch, setCodeSwitch] = useState(false);

	const [serviceTypes, setServiceTypes] = useState<string[]>([]);
	const [servicePlugins, setServicePlugins] = useState<Record<string, any>>({});
	const [serviceSettings, setServiceSettings] = useState<Record<string, any>>({});

	const [activeTab, setActiveTab] = useState<string>(BASIC);

	const methods = useForm<any>({
		defaultValues: { name: "", description: "", prompt: "", settings: {} },
	});

	const { handleSubmit, setError, reset, control, setValue, getValues, formState } = methods;

	const fetchServiceSettings = async (service: string, plugin: string) => {
		if (!plugin) {
			setServiceSettings((prev) => ({ ...prev, [service]: null }));
			return;
		}

		try {
			const response = await apiClient.get(`/plugin-services/${service}/${plugin}`);
			setServiceSettings((prev) => ({ ...prev, [service]: response.data }));

			// Only seed defaults where the form has nothing yet, so switching tabs on an
			// existing persona does not wipe settings that were loaded from the server.
			const current = getValues()?.[service]?.settings ?? {};
			setValue(
				`${service}.settings`,
				Object.fromEntries(
					response.data.map((setting: any) => [
						setting.name,
						current[setting.name] === undefined ? setting.value : current[setting.name],
					])
				)
			);
		} catch (error) {
			logger.error(error);
			toast.error("Could not load the settings for that plugin");
		}
	};

	const fetchServicePlugins = async (types: string[]) => {
		setIsLoadingPlugins(true);
		try {
			await Promise.all(
				types.map(async (type) => {
					const response = await apiClient.get(`/plugin-services/${type}`);
					setServicePlugins((prev) => ({ ...prev, [type]: response.data }));

					const pluginId = getValues()?.[type]?.pluginId || response.data?.[0]?.id;
					if (pluginId) {
						if (!getValues()?.[type]?.pluginId) setValue(`${type}.pluginId`, pluginId);
						await fetchServiceSettings(type, pluginId);
					}
				})
			);
		} catch (error) {
			logger.error(error);
			toast.error("Could not load the available plugins");
		} finally {
			setIsLoadingPlugins(false);
		}
	};

	const load = async () => {
		try {
			// The server decides which services a persona may have, so the tabs cannot drift
			// from what it will accept.
			const typesResponse = await apiClient.get("/personas/service-types");
			const types: string[] = typesResponse.data;
			setServiceTypes(types);

			if (isEdit) {
				const response = await apiClient.get(`/personas/${personaId}`);
				setPersonaName(response.data.name);
				setLanguages(response.data.settings?.languages ?? []);
				setCodeSwitch(Boolean(response.data.settings?.codeSwitch));
				reset({
					name: response.data.name,
					description: response.data.description ?? "",
					prompt: response.data.prompt,
					settings: response.data.settings ?? {},
					...Object.fromEntries(
						(response.data.services ?? []).map((service: any) => [
							service.type,
							{ pluginId: service.plugin.id, settings: service.settings },
						])
					),
				});
				setIsLoadingPersona(false);
			}

			await fetchServicePlugins(types);
		} catch (error) {
			logger.error(error);
			toast.error("Could not load this persona");
			setIsLoadingPersona(false);
			setIsLoadingPlugins(false);
		}
	};

	useLayoutEffect(() => {
		void load();
	}, [personaId]);

	const onSubmit = async (values: any) => {
		if (!values.name || !values.prompt) {
			if (!values.name) setError("name", { message: "Name is required" });
			if (!values.prompt) setError("prompt", { message: "A description of the caller is required" });
			setActiveTab(BASIC);
			return;
		}

		const body = {
			name: values.name,
			description: values.description || null,
			prompt: values.prompt,
			settings: {
				...values.settings,
				languages,
				// Meaningless with fewer than two languages, so it is never stored as true there.
				codeSwitch: languages.length > 1 && codeSwitch,
				// An empty box means "no opening line", not an empty utterance.
				...(values.settings?.firstPhrase ? {} : { firstPhrase: undefined }),
				...(values.settings?.maxTurns ? { maxTurns: Number(values.settings.maxTurns) } : { maxTurns: undefined }),
			},
			services: serviceTypes
				.filter((type) => values?.[type]?.pluginId)
				.map((type) => ({
					type,
					pluginId: values[type].pluginId,
					settings: values[type].settings ?? {},
				})),
		};

		try {
			await (isEdit ? apiClient.put(`/personas/${personaId}`, body) : apiClient.post("/personas", body));
			toast.success(isEdit ? "Persona updated" : "Persona created");
			queryClient.invalidateQueries({ queryKey: personasKeys.lists() });
			if (personaId) queryClient.invalidateQueries({ queryKey: personasKeys.detail(personaId) });
			navigate("/personas");
		} catch (error: any) {
			const messages = error?.response?.data?.message;
			const message = Array.isArray(messages) ? messages[0] : messages;

			// Validation failures come back keyed by the field that failed, e.g.
			// "services.1.settings.model ..." — take the caller to the tab that owns it.
			const key: string = message?.split(" ")[0] ?? "";
			const serviceIndex = Number(key.split(".")[1]);
			if (key.startsWith("services.") && Number.isInteger(serviceIndex)) {
				const owning = serviceTypes.filter((type) => getValues()?.[type]?.pluginId)[serviceIndex];
				if (owning) setActiveTab(owning);
			} else {
				setActiveTab(BASIC);
			}

			toast.error(message || "Something went wrong");
		}
	};

	const isLoading = isLoadingPersona || isLoadingPlugins;
	const tabs = [
		{ label: "Main", value: BASIC },
		...serviceTypes.map((type) => ({ label: SERVICE_LABELS[type] ?? type, value: type })),
	];

	return (
		<Box
			sx={{
				maxWidth: "var(--Content-maxWidth)",
				m: "var(--Content-margin)",
				p: "var(--Content-padding)",
				width: "var(--Content-width)",
			}}
		>
			<Stack spacing={4}>
				<Stack direction="column" spacing={3}>
					<Breadcrumbs>
						<Link
							color="text.primary"
							component={RouterLink}
							href="/personas"
							sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
							variant="subtitle2"
						>
							<ArrowLeftIcon fontSize="var(--icon-fontSize-md)" />
							Personas
						</Link>
						{isEdit ? (
							<>
								<Typography variant="subtitle2" color="text.secondary" whiteSpace="pre">
									/
								</Typography>
								{isLoading ? (
									<CustomSkeleton width={100} height={20} />
								) : (
									<Typography variant="subtitle2" color="text.secondary">
										{personaName}
									</Typography>
								)}
							</>
						) : null}
						<Typography variant="subtitle2" color="text.secondary" whiteSpace="pre">
							/
						</Typography>
						<Typography variant="subtitle2" color="text.secondary">
							{isEdit ? "Edit" : "Create"}
						</Typography>
					</Breadcrumbs>

					<Typography variant="h4">{isEdit ? "Edit Persona" : "Create Persona"}</Typography>
				</Stack>

				<Card sx={{ position: "relative" }}>
					<ComponentLoader loading={isLoading} />
					<Tabs
						onChange={(_, value: string) => setActiveTab(value)}
						sx={{ px: 3 }}
						value={activeTab}
						variant="scrollable"
					>
						{tabs.map((tab) => (
							<Tab
								iconPosition="end"
								key={tab.value}
								label={tab.label}
								sx={{ minHeight: "auto" }}
								tabIndex={0}
								value={tab.value}
							/>
						))}
					</Tabs>

					<Divider />

					<Box sx={{ p: 2, py: 2.5 }}>
						<FormProvider {...methods}>
							{activeTab === BASIC ? (
								<BasicInformation
									control={control}
									languages={languages}
									onLanguagesChange={setLanguages}
									codeSwitch={codeSwitch}
									onCodeSwitchChange={setCodeSwitch}
								/>
							) : (
								<Stack spacing={2}>
									{SERVICE_HINTS[activeTab] ? <Alert severity="info">{SERVICE_HINTS[activeTab]}</Alert> : null}
									<Service
										control={control}
										service={activeTab}
										plugins={servicePlugins?.[activeTab]}
										onPluginChange={fetchServiceSettings}
										settings={serviceSettings?.[activeTab]}
									/>
								</Stack>
							)}

							<Button
								onClick={handleSubmit(onSubmit)}
								variant="contained"
								disabled={formState.isSubmitting || isLoading}
								sx={{ mt: 2.5 }}
							>
								{isEdit ? "Update Persona" : "Create Persona"}
							</Button>
						</FormProvider>
					</Box>
				</Card>
			</Stack>
		</Box>
	);
}
